import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import type { SellerProfile } from "@/types/profiles";
import type { SellerOffer } from "@/types/offers";

interface Review {
  id: string;
  reviewer_id: string;
  reviewer_name: string;
  rating: number;
  text: string;
  created_at: string;
}

// Helper function to validate if a string is a valid UUID
const isValidUUID = (id: string): boolean => {
  if (!id) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export function useSellerData(sellerIdOrSlug: string) {
  // Skip the query if we don't have a valid identifier
  const shouldFetchData = !!sellerIdOrSlug && sellerIdOrSlug !== "";
  
  // Check if it's a UUID or a slug
  const queryField = isValidUUID(sellerIdOrSlug) ? "id" : "slug";
  
  const { data: seller, isLoading: isLoadingSeller, error: sellerError } = useQuery({
    queryKey: ["seller", sellerIdOrSlug],
    queryFn: async () => {
      try {
        // If we can't determine a query, return null
        if (!sellerIdOrSlug || sellerIdOrSlug === "") {
          return null;
        }

        const client = await getSupabaseClient();
        const { data, error } = await client
          .from("seller_profiles")
          .select("*")
          .eq(queryField, sellerIdOrSlug)
          .single();

        if (error) {
          console.error(`Error fetching seller with ${queryField}=${sellerIdOrSlug}:`, error);
          throw error;
        }
        
        if (!data) {
          return null;
        }
        
        return data as SellerProfile;
      } catch (err) {
        console.error("Error fetching seller:", err);
        toast({
          title: "Error",
          description: "Failed to load seller profile",
          variant: "destructive",
        });
        throw err;
      }
    },
    enabled: shouldFetchData,
  });

  const { data: offers, isLoading: isLoadingOffers, error: offersError } = useQuery({
    queryKey: ["sellerOffers", seller?.id],
    queryFn: async () => {
      try {
        // Skip query if we don't have a valid seller
        if (!seller?.id) {
          return [];
        }

        const client = await getSupabaseClient();
        const { data, error } = await client
          .from("offers")
          .select(`
            *,
            seller_profiles (
              name,
              verified,
              slug
            )
          `)
          .eq("seller_profile_id", seller.id)
          .eq("is_active", true)
          .order("created_at", { ascending: false });

        if (error) {
          throw error;
        }
        
        return (data || []).map(offer => ({
          ...offer,
          seller_profile: {
            name: offer.seller_profiles?.name || "Unknown",
            verified: offer.seller_profiles?.verified || false,
            slug: offer.seller_profiles?.slug
          },
          seller_profile_id: seller.id
        })) as SellerOffer[];
      } catch (err) {
        console.error("Error fetching offers:", err);
        toast({
          title: "Error",
          description: "Failed to load seller offers",
          variant: "destructive",
        });
        throw err;
      }
    },
    enabled: !!seller?.id,
  });

  const { data: reviews, isLoading: isLoadingReviews, error: reviewsError } = useQuery({
    queryKey: ["sellerReviews", seller?.id],
    queryFn: async () => {
      try {
        // Skip query if we don't have a valid seller
        if (!seller?.id) {
          return [];
        }

        const client = await getSupabaseClient();
        const { data, error } = await client.rpc('get_seller_reviews', {
          seller_id: seller.id
        });

        if (error) {
          console.error(`Error fetching reviews for seller ID ${seller.id}:`, error);
          return [];
        }
        
        return (data || []).map((review: any) => ({
          id: review.id,
          reviewer_id: review.reviewer_id,
          reviewer_name: review.reviewer_name || "Anonymous",
          rating: review.rating,
          text: review.comment || "",
          created_at: review.created_at,
        })) as Review[];
      } catch (err) {
        console.error("Error in review query:", err);
        return [];
      }
    },
    enabled: !!seller?.id,
  });

  const isLoading = isLoadingSeller || isLoadingOffers || isLoadingReviews;
  const hasError = sellerError || offersError || reviewsError;

  return {
    seller,
    offers,
    reviews,
    isLoading,
    hasError
  };
}
